import React, { useEffect, useState } from 'react';
import {
  Alert,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Col,
  Row,
  FormGroup,
  Input,
  Label,
  Button
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import CountryDropdown from '../../Invoices/Templates/CountryDropdown';
import { shippingApi } from '../../../api/shipping';
import CountryPhoneReact from './CountryPhoneReact';
import 'bootstrap/dist/css/bootstrap.min.css';
import './shipping-location-modal.scss';

const ShippingLocationModal = ({
  isOpen,
  onClose,
  onData,
  houseID,
}) => {
  const defaultContact = {
    contactName: '',
    contactEmail: '',
    callingCode: '',
    countryCode: '',
    phoneNumber: '',
  };
  const initialFormData = {
    locationName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    contacts: [
      defaultContact
    ],
    accessInstructions: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [apiErrorMessage, setApiErrorMessage] = useState(null);
  const [apiSuccessMessage, setApiSuccessMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const [shouldShowState, setShouldShowState] = useState(formData.country.value !== 'US' || formData.country.value !== 'CA');

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormData);
      setErrors({});
    }
  }, [isOpen]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const PHONE_REGEX = /^\+?\d{10,15}$/;

    const generalRequiredFields = {
      locationName: 'Facility Name is required.',
      address1: 'Street Address 1 is required.',
      city: 'City is required.',
      zip: 'Postal code is required.',
      state: 'State / Province is required.',
    };

    const fieldsToSkip = ['address2', 'accessInstructions'];
    // Validate contact section helper
    const validateContact = (contact, index) => {
      if (!contact.phoneNumber || contact.phoneNumber.trim() === '') {
        newErrors[`phoneNumber-${index}`] = 'Contact phone number is required.';
      } else if (!PHONE_REGEX.test(contact.phoneNumber)) {
        newErrors[`phoneNumber-${index}`] = 'Invalid phone number.';
      }
      if (!contact.contactName || contact.contactName.trim() === '') {
        newErrors[`contactName-${index}`] = 'Contact name is required.';
      }
      if (!contact.contactEmail || contact.contactEmail.trim() === '') {
        newErrors[`contactEmail-${index}`] = 'Contact email is required.';
      } else if (!EMAIL_REGEX.test(contact.contactEmail)) {
        newErrors[`contactEmail-${index}`] = 'Invalid email format.';
      }
    };

    // Validate required fields
    Object.entries(formData).forEach(([field, value]) => {
      if (fieldsToSkip.includes(field)) return;

      switch (field) {
        case 'contacts':
          value.forEach((contact, index) => {
            validateContact(contact, index);
          });
          break;
        default:
          const actualValue = typeof value === 'object' && value !== null && 'value' in value
            ? value.value
            : value;
          if (
            typeof actualValue === 'string' && (!actualValue.trim())
          ) {
            newErrors[field] = generalRequiredFields[field] || `${field} is required.`;
          }
          break;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle country dropdown helper function
  const handleCountryDropdownChange = (data, key) => {
    if (key === 'country') {
      const countryHasState = data.value === 'US' || data.value === 'CA';
      setShouldShowState(!countryHasState);
    }
    setFormData((prevData) => ({ ...prevData, [key]: data }));
  };

  // Handle state / province update if not CountryDropdown does
  const handleStateSelection = (e) => {
    const value = e.target.value;
    setFormData(prevData => ({ ...prevData, state: { value, label: value } }));
  };

  // Handle all input changes
  const handleInputChange = (e, contactIndex = null, customField = null, customValue = null, callingCode = null) => {
    const { name, value, type, checked } = e?.target || {};

    setFormData((prevData) => {
      let updatedContacts = [...prevData.contacts];

      // Update fields in the contact section
      if (contactIndex !== null) {
        updatedContacts = updatedContacts.map((contact, index) => {
          if (index !== contactIndex) return contact;

          if (customField && customValue !== undefined) {
            if (callingCode !== null) {
              contact.callingCode = callingCode;
            }
            return { ...contact, [customField]: customValue };
          }

          return { ...contact, [name]: value };
        });
      }
      // Update non-contact fields
      else {
        return {
          ...prevData,
          [name]: value,
        };
      }

      return { ...prevData, contacts: updatedContacts };
    });
  };

  const handleAddContact = () => {
    setFormData((prevData) => ({
      ...prevData,
      contacts: [...prevData.contacts, defaultContact],
    }));
  };

  const handleRemoveContact = (index) => {
    const filteredContacts = formData.contacts.filter((_, filterIndex) => filterIndex !== index);
    const contacts = filteredContacts.length ? filteredContacts : [defaultContact];
    setFormData({ ...formData, contacts });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const updateContacts = formData.contacts.map((contact) => ({
          fullName: contact.contactName,
          email: contact.contactEmail,
          phoneNumber: `+${contact.callingCode}${contact.phoneNumber}`
        }));

        const getValue = (field) => (typeof field === 'object' ? field?.value : field || '');

        const dataToSave = {
          origin: {
            name: formData.locationName?.trim() ? formData.locationName : null,
            accessInstructions: formData.accessInstructions?.trim() ? formData.accessInstructions : null,
            address: {
              addressLine1: formData.address1,
              addressLine2: formData.address2?.trim() ? formData.address2 : null,
              city: formData.city,
              state: getValue(formData.state),
              postalCode: formData.zip,
              country: formData.country.value
            },
            contacts: updateContacts

          }
        };

        const accountID = houseID;
        const accountDomain = 'AUCTIONHOUSE';

        await shippingApi.saveShippingLocation(dataToSave, accountID, accountDomain);
        setApiSuccessMessage('Shipping fulfillment location saved successfully!');
        onClose();
        onData(dataToSave.origin.name);
      } catch (error) {
        setApiErrorMessage('An error occurred while saving. Please try again.');
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Separating this from the rest of the jsx so to dix the layout issue with
  // country & state / province dropdowns from the <CountryDropdown/> component
  const postalCodeComponent = (
    <FormGroup className={`form-group ${!formData.zip ? 'form-error' : ''}`}>
      <Label htmlFor="zip" className="form-label">
        Postal Code*
      </Label>
      <Input
        id="zip"
        name="zip"
        value={formData.zip}
        onChange={handleInputChange}
        maxLength={100}
        className="form-control"
        invalid={!!errors.zip}
      />
      <FormFeedback role="alert">{errors.zip}</FormFeedback>
    </FormGroup>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} toggle={onClose} className="edit-location-modal">
      <ModalHeader className="modal-header" tag="h1">
        Add Fulfillment Location
      </ModalHeader>
      <button type="button" className="close modal-close-icon" onClick={handleClose} aria-label="Close shipping fulfillment location modal">
        <span aria-hidden="true">×</span>
      </button>
      <p>
        Add the address your sold items may ship from. You will be able to customize
        different locations at the lot level during the final stage of review.
      </p>
      {Object.keys(errors).length > 0 && (
        <Alert color="danger" className="mt-2" role="alert">
          Please fix the highlighted errors to save changes.
        </Alert>
      )}
      {apiErrorMessage && (
      <Alert color="danger" className="mt-2" role="alert">
        <span>{apiErrorMessage}</span>
      </Alert>
      )}
      {apiSuccessMessage && (
      <Alert color="success" className="mt-2" role="alert">
        <span>{apiSuccessMessage}</span>
      </Alert>
      )}
      <ModalBody className="modal-body">
        <Row>
          <Col xs={12}>
            <FormGroup
              className={`form-group ${!formData.locationName ? 'form-error' : ''}`}
            >
              <Label htmlFor="locationName" className="form-label">
                Facility Name*
              </Label>
              <Input
                id="locationName"
                name="locationName"
                value={formData.locationName}
                onChange={handleInputChange}
                maxLength={255}
                className="form-control"
                invalid={!!errors.locationName}
              />
            </FormGroup>
            <FormFeedback role="alert">{errors.locationName}</FormFeedback>
          </Col>
          <Col xs={12}>
            <FormGroup
              className={`form-group ${!formData.address1 ? 'form-error' : ''}`}
            >
              <Label htmlFor="address1" className="form-label">
                Street Address 1*
              </Label>
              <Input
                id="address1"
                name="address1"
                value={formData.address1}
                onChange={handleInputChange}
                maxLength={255}
                className="form-control"
                invalid={!!errors.address1}
              />
              <FormFeedback role="alert">{errors.address1}</FormFeedback>
            </FormGroup>
          </Col>
          <Col xs={12}>
            <FormGroup
              className={`form-group ${!formData.address2 ? 'form-error' : ''}`}
            >
              <Label htmlFor="address2" className="form-label">
                Street Address 2
              </Label>
              <Input
                id="address2"
                name="address2"
                value={formData.address2}
                onChange={handleInputChange}
                maxLength={255}
                className="form-control"
              />
            </FormGroup>
          </Col>
          <Col xs={6}>
            <FormGroup className={`form-group ${!formData.city ? 'form-error' : ''}`}>
              <Label htmlFor="city" className="form-label">
                City*
              </Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                maxLength={255}
                className="form-control"
                invalid={!!errors.city}
              />
              <FormFeedback role="alert">{errors.city}</FormFeedback>
            </FormGroup>
            {!shouldShowState && postalCodeComponent}
          </Col>
          <Col xs={6}>
            <CountryDropdown
              dropdownOnchangeHandler={handleCountryDropdownChange}
              headingSize="p"
              countryColSize={13}
              stateColSize={13}
              headingClass="country-dropdown-label"
              stateClass="country-dropdown-state"
              errorClass="invalid-feedback"
              hasCountryError={errors.country}
              hasStateError={errors.state}
              isShippingCountryDropdown={true}
            />
          </Col>
          {shouldShowState && (
          <Col xs={6}>
            {postalCodeComponent}
          </Col>
          )}
          {shouldShowState && (
          <Col xs={6}>
            <FormGroup className={`form-group ${!formData.state ? 'form-error' : ''}`}>
              <Label htmlFor="state" className="form-label">
                State/Province*
              </Label>
              <Input
                id="state"
                name="state"
                value={formData.state?.value ?? ''}
                onChange={handleStateSelection}
                maxLength={100}
                className="form-control"
                invalid={!!errors.state}
              />
              <FormFeedback role="alert">{errors.state}</FormFeedback>
            </FormGroup>
          </Col>
          )}
          <Col xs={12}>
            <FormGroup
              className={`onsite-instructions form-group ${!formData.deliveryInstructions ? 'form-error' : ''}`}
            >
              <Label htmlFor="accessInstructions" className="form-label">
                Onsite Instructions
              </Label>
              <Input
                id="accessInstructions"
                name="accessInstructions"
                value={formData.accessInstructions}
                onChange={handleInputChange}
                maxLength={100}
                className="form-control"
              />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <div className="contact-info-section">
        <ModalHeader tag="h1">Contact Info</ModalHeader>
        <p>
          Add the contact person for this fulfillment location. You may add more than one.
        </p>
        <ModalBody className="modal-body">
          <Row>
            {formData.contacts.map((contact, index) => (
              <Col xs={12} key={contact.id || index} className={`mb-2 ${index > 0 ? 'contact-border-top' : ''}`}>
                <div className="contact-section">
                  <FormGroup>
                    <Label htmlFor={`contactname-${index}`} className={`form-label ${index > 0 ? 'contact-label' : ''}`}>
                      Contact Name*
                    </Label>
                    <Input
                      id={`contactName-${index}`}
                      name="contactName"
                      value={contact.contactName}
                      onChange={(e) => handleInputChange(e, index)}
                      maxLength={100}
                      className={errors[`contactName-${index}`] ? 'is-invalid' : 'form-control'}
                    />
                    {errors[`contactName-${index}`] && (
                    <FormFeedback role="alert">{errors[`contactName-${index}`]}</FormFeedback>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor={`contactEmail-${index}`} className="form-label">
                      Contact Email*
                    </Label>
                    <Input
                      id={`contactEmail-${index}`}
                      name="contactEmail"
                      value={contact.contactEmail}
                      onChange={(e) => handleInputChange(e, index)}
                      maxLength={100}
                      className={errors[`contactEmail-${index}`] ? 'is-invalid' : 'form-control'}
                    />
                    {errors[`contactEmail-${index}`] && (
                    <FormFeedback role="alert">{errors[`contactEmail-${index}`]}</FormFeedback>
                    )}
                  </FormGroup>

                  <FormGroup className={errors[`phoneNumber-${index}`] ? '' : 'phone-input-section'}>
                    <Label htmlFor={`phoneNumber-${index}`} className="form-label">
                      Mobile Phone*
                    </Label>
                    <CountryPhoneReact
                      id={`phoneNumber-${index}`}
                      currentCountryCode={contact.countryCode}
                      onPhoneChange={(value) => handleInputChange(null, index, 'phoneNumber', value)}
                      onCountryChange={(countryCode, callingCode) => handleInputChange(null, index, 'countryCode', countryCode, callingCode)}
                      errors={errors}
                      index={index}
                      isInvalidPhone={!!errors[`phoneNumber-${index}`]}
                      isInvalidCountryCode={!!errors[`countryCode-${index}`]}
                    />
                    {errors[`phoneNumber-${index}`] && (
                    <FormFeedback role="alert">{errors[`phoneNumber-${index}`]}</FormFeedback>
                    )}
                  </FormGroup>
                  {formData.contacts.length > 1 && (
                  <>
                    <div className="contact-section">
                      <button
                        type="button"
                        className="delete-contact-section"
                        onClick={() => handleRemoveContact(index)}
                        aria-label={`Delete shipping fulfillment location contact ${index}`}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} size="sm" className="delete-contact-icon" />
                        Delete contact
                      </button>
                    </div>
                  </>
                  )}
                </div>
              </Col>
            ))}
          </Row>
          <Button color="link" onClick={handleAddContact} className="add-contact-button" aria-label="Add shipping fulfillment location contact">
            <AddIcon color="primary" />Add Contact
          </Button>
        </ModalBody>
      </div>
      <ModalFooter>
        <Button color="link" onClick={handleClose} aria-label="Cancel shipping fulfillment location modal changes">
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} aria-label="Save shipping fulfillment location modal location">
          Save Changes
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ShippingLocationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  houseID: PropTypes.number.isRequired
};

export default ShippingLocationModal;
